/**
 * The documents and email tabs of the shop file (work order items 3 and 4), minus the JSX.
 *
 * JSX-free like the rest of `src/lib`: the dashboard's Jest runs in `node` with no jsdom, so which
 * action a row offers, what a refusal code means and how an email status is worded are only
 * testable out here.
 *
 * Every catalogue key is spelled out as a literal rather than built from a token, so the parity
 * spec's source scan sees — and resolves — each one.
 */

import { isReasonAcceptable } from './shop-file';

/** The row fields the action rules read — a subset of the generated `OpsDocumentRowDto`. */
export interface DocumentActionInput {
  readonly hasPdf: boolean;
  readonly heldForManualSend: boolean;
  readonly customerEmail?: string | null;
  /** The document kind (`invoice`, `creditNote`, `cancellation`); only an invoice can be reissued. */
  readonly kind?: string;
  /** False once a Storno superseded the invoice. */
  readonly isActive?: boolean;
  /** The invoice is issued (archived PDF or sent) and can only be changed by a Storno. */
  readonly isImmutable?: boolean;
}

/** What a row offers, and why an action is not offered. */
export interface DocumentActions {
  readonly canDownload: boolean;
  readonly canResend: boolean;
  /** Catalogue key explaining a disabled resend, or null when it is enabled. */
  readonly resendBlockedKey: string | null;
  /**
   * Resend only works with an alternative address: the document carries no customer email, so
   * the dialog must ask for one (and for the ticket that authorises it).
   */
  readonly resendNeedsOverride: boolean;
  readonly canRegenerate: boolean;
  /** Catalogue key explaining a disabled regenerate, or null when it is enabled. */
  readonly regenerateBlockedKey: string | null;
  /** Reissue (Storno + new invoice from the current order): active, immutable invoices only. */
  readonly canReissue: boolean;
  /** Catalogue key explaining why an INVOICE row cannot be reissued; null for other kinds too. */
  readonly reissueBlockedKey: string | null;
}

/**
 * Which actions a document row offers.
 *
 * - **Download** needs a stored PDF.
 * - **Resend** sends the ARCHIVED bytes, so it needs a stored PDF, and it is never the first send
 *   of a document the shop holds for its own manual send (A9) — the API refuses that with a 409;
 *   the button says why before anyone gets that far.
 * - **Regenerate** exists only for a document WITHOUT a PDF. An archived PDF is never
 *   re-rendered (§ 147 AO, D4), so on a row with one the action is not offered at all.
 * - **Reissue** is for an INVOICE that is active and immutable: it supersedes the document by a
 *   Storno and mints a new one from the order's current data. A superseded invoice has an active
 *   successor to reissue instead; one that is not yet immutable owes no Storno (the next order
 *   update rewrites it). The API refuses both; the row says why first.
 *
 * @param row - The document row.
 */
export function documentActions(row: DocumentActionInput): DocumentActions {
  const resendBlockedKey = !row.hasPdf
    ? 'opsDocuments.actions.resendNeedsPdf'
    : row.heldForManualSend
      ? 'opsDocuments.actions.resendHeld'
      : null;
  const reissueBlockedKey =
    row.kind !== 'invoice'
      ? null
      : row.isActive === false
        ? 'opsDocuments.actions.reissueSuperseded'
        : row.isImmutable === false
          ? 'opsDocuments.actions.reissueNotImmutable'
          : null;
  return {
    canDownload: row.hasPdf,
    canResend: resendBlockedKey === null,
    resendBlockedKey,
    resendNeedsOverride: !row.customerEmail?.trim(),
    canRegenerate: !row.hasPdf,
    regenerateBlockedKey: row.hasPdf
      ? 'opsDocuments.actions.regenerateHasPdf'
      : null,
    canReissue: row.kind === 'invoice' && reissueBlockedKey === null,
    reissueBlockedKey,
  };
}

/** What the reissue form holds. */
export interface ReissueInput {
  readonly reason: string;
  /** The customer's USt-IdNr.; empty for "none — reissue as the order stands". */
  readonly customerVatId: string;
  /** The merchant confirmed the CUSTOMER supplied the number. */
  readonly customerSuppliedVatId: boolean;
  /** The customer declared the number applies to all their future business orders. */
  readonly standingDeclaration: boolean;
}

/**
 * A deliberately loose USt-IdNr. shape — two letters, then 2–13 alphanumerics, spaces ignored.
 * The API runs the real per-country format check; this only keeps the confirm button from arming
 * on an obvious typo.
 */
const VAT_ID_SHAPE = /^[A-Z]{2}[A-Z0-9]{2,13}$/;

/**
 * The first problem with a reissue form, as a catalogue key, or null when it may be submitted.
 *
 * A VAT ID needs the merchant's confirmation that the customer supplied it (§ 6a Abs. 1 Nr. 4
 * UStG makes the number a material condition, and its provenance is the merchant's to vouch
 * for). The API refuses the combination too; asking here saves the round trip.
 *
 * @param input - The form.
 */
export function reissueProblem(input: ReissueInput): string | null {
  if (!isReasonAcceptable(input.reason)) {
    return 'opsDocuments.form.reasonTooShort';
  }
  const vatId = normaliseVatId(input.customerVatId);
  if (!vatId) return null;
  if (!VAT_ID_SHAPE.test(vatId)) return 'opsDocuments.form.vatIdInvalid';
  if (!input.customerSuppliedVatId) {
    return 'opsDocuments.form.vatIdNeedsConfirmation';
  }
  return null;
}

/** Upper-cased, spaces removed — the shape the API stores. */
export function normaliseVatId(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

/**
 * The reissue request body. The VAT fields are left out entirely when no number was typed, so
 * the API's optional validators never see an empty string.
 *
 * @param input - A form {@link reissueProblem} accepted.
 */
export function reissueBody(input: ReissueInput): {
  reason: string;
  customerVatId?: string;
  customerSuppliedVatId?: boolean;
  standingDeclaration?: boolean;
} {
  const vatId = normaliseVatId(input.customerVatId);
  return {
    reason: input.reason.trim(),
    ...(vatId
      ? {
          customerVatId: vatId,
          customerSuppliedVatId: true,
          standingDeclaration: input.standingDeclaration,
        }
      : {}),
  };
}

/**
 * Catalogue key of a preview refusal (`OpsReissueRefusal`), or null for a code the dashboard does
 * not know — the list comes from the API, so a new refusal must not crash the form.
 *
 * @param refusal - The preview's `refusal`, if any.
 */
export function reissueRefusalKey(
  refusal: string | null | undefined,
): string | null {
  switch (refusal) {
    case 'INVOICE_SUPERSEDED':
      return 'opsDocuments.errors.reissueSuperseded';
    case 'INVOICE_NOT_IMMUTABLE':
      return 'opsDocuments.errors.reissueNotImmutable';
    case 'NUMBERING_NOT_CONFIGURED':
      return 'opsDocuments.errors.reissueNumberingNotConfigured';
    case 'CORRECTION_LOOP_STOPPED':
      return 'opsDocuments.errors.reissueLoopStopped';
    case 'SCOPES_NOT_APPROVED':
      return 'opsDocuments.errors.reissueScopesNotApproved';
    default:
      return null;
  }
}

/** What the resend form holds. */
export interface ResendInput {
  readonly reason: string;
  /** Alternative recipient; empty for "the customer address on the document". */
  readonly recipientOverride: string;
  /** The support ticket carrying the merchant's instruction; empty for none. */
  readonly ticketId: string;
}

/**
 * A deliberately loose address check — one `@`, something either side, a dot in the domain. The
 * API validates for real; this only keeps the confirm button from arming on an obvious typo.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The first problem with a resend form, as a catalogue key, or null when it may be submitted.
 *
 * An override needs a ticket (A7): sending a merchant's document to an address the merchant never
 * wrote down is only allowed on the merchant's documented instruction, and the ticket is that
 * instruction. The API refuses the combination too; asking here saves the round trip.
 *
 * @param input - The form.
 * @param needsOverride - The document has no customer email, so an override is mandatory.
 */
export function resendProblem(
  input: ResendInput,
  needsOverride: boolean,
): string | null {
  if (!isReasonAcceptable(input.reason)) {
    return 'opsDocuments.form.reasonTooShort';
  }
  const override = input.recipientOverride.trim();
  if (!override) {
    return needsOverride ? 'opsDocuments.form.overrideRequired' : null;
  }
  if (!EMAIL_SHAPE.test(override)) return 'opsDocuments.form.overrideInvalid';
  if (!input.ticketId.trim()) return 'opsDocuments.form.overrideNeedsTicket';
  return null;
}

/**
 * The resend request body. Blank fields are left out rather than sent empty, so the API's
 * `@IsEmail()` never sees an empty string.
 *
 * @param input - A form {@link resendProblem} accepted.
 */
export function resendBody(input: ResendInput): {
  reason: string;
  recipientOverride?: string;
  ticketId?: string;
} {
  const override = input.recipientOverride.trim();
  const ticketId = input.ticketId.trim();
  return {
    reason: input.reason.trim(),
    ...(override ? { recipientOverride: override } : {}),
    ...(ticketId ? { ticketId } : {}),
  };
}

/**
 * The refusal codes the documents and email endpoints answer with, mapped to their explanation.
 * Codes the dashboard does not know fall back to the server's own message.
 *
 * Two vocabularies meet here: the ops endpoints' own `SCREAMING_CASE` codes, and the camelCase
 * `MERCHANT_ERROR_CODES` the shared merchant resend/regenerate throws — the operator path calls
 * those methods, so their refusals arrive here unchanged.
 */
const ERROR_KEYS: Readonly<Record<string, string>> = {
  RECIPIENT_OVERRIDE_NEEDS_TICKET: 'opsDocuments.errors.overrideNeedsTicket',
  TICKET_NOT_OF_THIS_SHOP: 'opsDocuments.errors.ticketNotOfShop',
  DOCUMENT_HELD_FOR_MANUAL_SEND: 'opsDocuments.errors.heldForManualSend',
  PDF_ALREADY_EXISTS: 'opsDocuments.errors.pdfAlreadyExists',
  PDF_NOT_AVAILABLE: 'opsDocuments.errors.pdfNotAvailable',
  PDF_MISSING_IN_STORAGE: 'opsDocuments.errors.pdfMissingInStorage',
  OPERATOR_EMAIL_MISSING: 'opsEmail.errors.operatorEmailMissing',
  // The reissue's own refusals (`ops-invoice-reissue.service.ts`).
  INVOICE_SUPERSEDED: 'opsDocuments.errors.reissueSuperseded',
  INVOICE_NOT_IMMUTABLE: 'opsDocuments.errors.reissueNotImmutable',
  NUMBERING_NOT_CONFIGURED: 'opsDocuments.errors.reissueNumberingNotConfigured',
  CORRECTION_LOOP_STOPPED: 'opsDocuments.errors.reissueLoopStopped',
  SCOPES_NOT_APPROVED: 'opsDocuments.errors.reissueScopesNotApproved',
  SHOP_GRANT_UNREADABLE: 'opsDocuments.errors.reissueGrantUnreadable',
  VAT_ID_NEEDS_CUSTOMER_CONFIRMATION:
    'opsDocuments.form.vatIdNeedsConfirmation',
  VAT_ID_INVALID: 'opsDocuments.errors.reissueVatIdInvalid',
  VAT_ID_NO_CUSTOMER: 'opsDocuments.errors.reissueVatIdNoCustomer',
  SHOPIFY_ORDER_NOT_FOUND: 'opsDocuments.errors.reissueOrderNotFound',
  SETTLEMENT_CREDIT_NOTE: 'opsDocuments.errors.reissueSettlementCreditNote',
  TRANSFER_REFUSED: 'opsDocuments.errors.reissueTransferRefused',
  // The writer's recipient refusal (a masked marketplace buyer), as the merchant path codes it.
  resolveRecipientNotShared: 'opsDocuments.errors.reissueRecipientNotShared',
  pdfAlreadyExists: 'opsDocuments.errors.pdfAlreadyExists',
  pdfNotAvailable: 'opsDocuments.errors.pdfNotAvailable',
  pdfFileMissing: 'opsDocuments.errors.pdfMissingInStorage',
  noCustomerEmail: 'opsDocuments.errors.noCustomerEmail',
  resendFailed: 'opsDocuments.errors.resendFailed',
};

/**
 * The catalogue key for an API refusal code, or null when the dashboard has no copy for it.
 *
 * @param code - The `code` of the API's error body, if it carried one.
 */
export function errorKeyForCode(
  code: string | null | undefined,
): string | null {
  if (!code) return null;
  return ERROR_KEYS[code] ?? null;
}

/**
 * The `code` field of an API error body, read without asserting its shape.
 *
 * @param body - `error.response.data`, whatever it turned out to be.
 */
export function errorCodeOf(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const record: Record<string, unknown> = { ...body };
  return typeof record['code'] === 'string' ? record['code'] : null;
}

/**
 * The name a downloaded PDF is saved under, built from the document number rather than read off
 * `Content-Disposition` — the API is cross-origin and does not expose that header to scripts.
 * The prefix per kind is the one the merchant's own download uses, and slashes become dashes as on
 * the server (`RE/2026/1001` is a real numbering style).
 *
 * Takes the kind as a plain string: the generated `OpsDocumentKind` is a TS enum, whose members are
 * not assignable to a literal union, and a string keeps this module free of the generated client.
 *
 * @param kind - The document kind (`invoice`, `creditNote`, `cancellation`).
 * @param documentNumber - The number as the document is headed.
 */
export function pdfFileName(kind: string, documentNumber: string): string {
  const prefix =
    kind === 'creditNote'
      ? 'credit-note'
      : kind === 'cancellation'
        ? 'cancellation-invoice'
        : kind === 'invoice'
          ? 'invoice'
          : 'document';
  return `${prefix}-${documentNumber.replace(/[/\\]/g, '-')}.pdf`;
}

/** Catalogue key of a document kind's label. Unknown kinds get the generic "document". */
export function documentKindKey(kind: string): string {
  switch (kind) {
    case 'invoice':
      return 'opsDocuments.kind.invoice';
    case 'creditNote':
      return 'opsDocuments.kind.creditNote';
    case 'cancellation':
      return 'opsDocuments.kind.cancellation';
    default:
      return 'opsDocuments.kind.document';
  }
}

// ── Email ───────────────────────────────────────────────────────────────────

/**
 * What an `EmailDeliveryLog` status means, in words (AC-4.3).
 *
 * The raw token stays on screen — operators match it against the database — but next to it the
 * meaning, because `SENT` is the one word here that reads as more than it is: the mail server
 * ACCEPTED the message, which is not the same as it arriving. `BOUNCED` has no writer (D5) and so
 * no explanation beyond "not recorded".
 *
 * @param status - The raw status token.
 * @returns A catalogue key, or null for a token without copy.
 */
export function emailStatusKey(status: string): string | null {
  switch (status) {
    case 'SENT':
      return 'opsEmail.status.sent';
    case 'FAILED':
      return 'opsEmail.status.failed';
    case 'PENDING':
      return 'opsEmail.status.pending';
    case 'PROCESSING':
      return 'opsEmail.status.processing';
    case 'RETRYING':
      return 'opsEmail.status.retrying';
    default:
      return null;
  }
}

/** The badge tone of an email status. */
export function emailStatusTone(
  status: string,
): 'green' | 'red' | 'amber' | 'gray' {
  if (status === 'SENT') return 'green';
  if (status === 'FAILED') return 'red';
  if (status === 'RETRYING') return 'amber';
  return 'gray';
}

/** Catalogue key of what a log row is about. */
export function emailKindKey(kind: string): string {
  switch (kind) {
    case 'invoice':
      return 'opsDocuments.kind.invoice';
    case 'creditNote':
      return 'opsDocuments.kind.creditNote';
    case 'cancellation':
      return 'opsDocuments.kind.cancellation';
    case 'export':
      return 'opsEmail.kind.export';
    default:
      return 'opsEmail.kind.unknown';
  }
}

/**
 * Catalogue key naming one kind of mail the log never records (AC-4.4), or null for a token the
 * dashboard does not know yet — the list comes from the API, so a new entry must not crash it.
 */
export function notLoggedKey(token: string): string | null {
  switch (token) {
    case 'smtpTest':
      return 'opsEmail.notLogged.smtpTest';
    case 'datevInvite':
      return 'opsEmail.notLogged.datevInvite';
    case 'recipientConfirmation':
      return 'opsEmail.notLogged.recipientConfirmation';
    case 'exportPasswordNotice':
      return 'opsEmail.notLogged.exportPasswordNotice';
    case 'supportNotification':
      return 'opsEmail.notLogged.supportNotification';
    default:
      return null;
  }
}

/** Catalogue key of an SMTP test request's outcome. */
export function smtpTestOutcomeKey(outcome: string): string {
  switch (outcome) {
    case 'queued':
      return 'opsEmail.smtpTest.queued';
    case 'notApplicableFallbackRelay':
      return 'opsEmail.smtpTest.notApplicableFallbackRelay';
    case 'notApplicableSmtpIncomplete':
      return 'opsEmail.smtpTest.notApplicableSmtpIncomplete';
    default:
      return 'opsEmail.smtpTest.unknownOutcome';
  }
}

/** How often the SMTP test is polled, and for how long before the UI stops asking. */
export const SMTP_TEST_POLL_MS = 2_000;

/** Two minutes of polling. A test that is still queued after that is a stuck worker. */
export const SMTP_TEST_POLL_MAX = 60;

/** Whether a polled test status is final. `not_found` is final too — the job aged out. */
export function isSmtpTestSettled(status: string): boolean {
  return (
    status === 'completed' || status === 'failed' || status === 'not_found'
  );
}

/** Catalogue key of a polled SMTP test status. */
export function smtpTestStatusKey(status: string): string {
  switch (status) {
    case 'completed':
      return 'opsEmail.smtpTest.completed';
    case 'failed':
      return 'opsEmail.smtpTest.failed';
    case 'not_found':
      return 'opsEmail.smtpTest.notFound';
    default:
      return 'opsEmail.smtpTest.polling';
  }
}
